import { useEffect, useRef } from "react";
import { splitPivot } from "@/lib/tool-kit/study-more/speed-reading";

/** The flashing display: one word anchored on its pivot letter, or a short chunk centred. */
export function ReadingStage({
  chunk,
  anchor,
  placeholder,
}: {
  chunk: readonly string[] | null;
  /** Anchor a single word on its pivot letter (RSVP). */
  anchor: boolean;
  placeholder: string;
}) {
  let body: React.ReactNode = (
    <span className="text-muted-foreground text-sm">{placeholder}</span>
  );
  if (chunk && chunk.length === 1 && anchor) {
    const { before, pivot, after } = splitPivot(chunk[0] ?? "");
    body = (
      <span className="grid w-full grid-cols-[1fr_auto_1fr] items-baseline font-medium text-3xl text-foreground sm:text-4xl">
        <span className="text-right">{before}</span>
        <span className="text-primary">{pivot}</span>
        <span className="text-left">{after}</span>
      </span>
    );
  } else if (chunk) {
    body = (
      <span className="px-4 text-center font-medium text-2xl text-foreground sm:text-3xl">
        {chunk.join(" ")}
      </span>
    );
  }
  return (
    <div
      aria-hidden="true"
      className="relative flex h-40 items-center justify-center overflow-hidden rounded-lg border border-border bg-card"
    >
      {anchor ? (
        <>
          <span className="absolute top-0 left-1/2 h-4 w-px bg-primary/50" />
          <span className="absolute bottom-0 left-1/2 h-4 w-px bg-primary/50" />
        </>
      ) : null}
      {body}
    </div>
  );
}

const WINDOW_BLOCK = 100;
const WINDOW_SIZE = 400;

/** The text itself, with the current chunk highlighted and kept in view. */
export function PacerText({
  words,
  start,
  length,
  label,
}: {
  words: readonly string[];
  /** Index of the first highlighted word. */
  start: number;
  /** How many words are highlighted. */
  length: number;
  label: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const current = useRef<HTMLSpanElement>(null);
  const from = Math.max(
    0,
    Math.floor(start / WINDOW_BLOCK) * WINDOW_BLOCK - WINDOW_BLOCK
  );
  const to = Math.min(words.length, from + WINDOW_SIZE);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every move of the highlight
  useEffect(() => {
    const box = container.current;
    const mark = current.current;
    if (box && mark) {
      box.scrollTop = mark.offsetTop - box.clientHeight / 2;
    }
  }, [start, from]);

  return (
    <section
      aria-label={label}
      className="relative h-56 overflow-y-auto rounded-lg border border-border bg-card p-4 text-foreground leading-relaxed"
      ref={container}
    >
      {words.slice(from, to).map((word, offset) => {
        const index = from + offset;
        const active = index >= start && index < start + length;
        return (
          <span
            className={active ? "rounded bg-primary/20 px-0.5" : undefined}
            key={index}
            ref={index === start ? current : undefined}
          >
            {word}{" "}
          </span>
        );
      })}
    </section>
  );
}
